import { useEffect, useState } from 'react';
import BOLayout from '@/pages/back-office/BOLayout';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import SettingsTopBanner from '@/components/back-office/SettingsTopBanner';

const UserCreation = () => {
	const [canSubmit, setCanSubmit] = useState(false);
	const [email, setEmail] = useState('');
	const [firstname, setFirstname] = useState('');
	const [lastname, setLastname] = useState('');
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [phoneNumber, setPhoneNumber] = useState('');
	const [role, setRole] = useState('');
	const navigate = useNavigate();
	const { emailParam } = useParams();
	const userRole = JSON.parse(localStorage.getItem('role'));

	useEffect(() => {
		if (userRole && userRole !== 'super-admin') {
			toast.info('Zone interdite', {
				position: 'top-center',
				autoClose: 1000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});

			navigate('/back-office');

			return;
		}
	}, [userRole]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(
					`${import.meta.env.VITE_API_URL}/api/back-office/settings/users/${emailParam}/view`
				);

				if (response.status === 200) {
					setEmail(response.data.email);
					setFirstname(response.data.firstname);
					setLastname(response.data.lastname);
					setPhoneNumber(response.data.phone_number);
					setRole(response.data.role);
				}
			} catch (error) {
				console.error('Error fetching user:', error);
			}
		};

		fetchData();
	}, []);

	useEffect(() => {
		if (email && password && confirmPassword && phoneNumber && role) {
			setCanSubmit(true);
		}

		if (
			password !== confirmPassword ||
			!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(email) ||
			!/^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/.test(phoneNumber)
		) {
			setCanSubmit(false);
		}
	}, [email, password, confirmPassword, phoneNumber, role]);

	const handleSubmit = async (e) => {
		e.preventDefault();

		const formData = new FormData();

		formData.append('firstname', firstname || undefined);
		formData.append('lastname', lastname || undefined);
		formData.append('email', email || undefined);
		formData.append('password', password || undefined);
		formData.append('phone_number', phoneNumber || undefined);
		formData.append('role_id', role || undefined);;

		try {
			const response = await axios.put(
				`${import.meta.env.VITE_API_URL}/api/back-office/settings/users/${emailParam}/update`,
				formData
			);

			if (response.status === 200) {
				toast.success('Utilisateur modifié avec succès', {
					position: 'top-center',
					autoClose: 800,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: false,
					draggable: true,
					theme: 'colored',
				});
			}
		} catch (error) {
			console.error('Error creating user:', error);
			let message = '';

			if (error?.response?.data?.error?.code === 'auth/phone-number-already-exists') {
				message = 'Ce numéro de téléphone est deja associé à un compte.';

			} else if (error?.response?.data?.error?.code === 'auth/invalid-phone-number') {
				message = 'Le numéro de téléphone fourni est invalide.';
			} else {
				message = 'Une erreur est survenue lors de la création de l\'utilisateur.';
			}
			console.log(error);

			toast.error(message, {
				position: 'top-center',
				autoClose: 1500,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});
		}

		setTimeout(() => {
			navigate('/back-office/settings/users/list');
		}, 1000);
	};

	return (
		<BOLayout>
			<div className="mb-12 grid grid-cols-1 gap-12">
				<SettingsTopBanner />
				<h1 className="text-3xl font-bold text-center">Ajoutez un utilisateur</h1>
			</div>

			<form className="text-center">
				{password && confirmPassword && password !== confirmPassword && (
					<p className="text-red-500">Les mots de passe ne sont pas identiques</p>
				)}

				{phoneNumber && !/^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/.test(phoneNumber) && (
					<p className="text-red-500">Le numéro de téléphone n'est pas valide</p>
				)}

				{email && !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(email) && (
					<p className="text-red-500">L'email n'est pas valide</p>
				)}

				<div className="grid grid-cols-1 gap-2 lg:grid-cols-3">
					<div className="flex flex-col">
						<label htmlFor="email" className="text-left pl-2">
							Email
						</label>
						<input
							type="email"
							name="email"
							id="email"
							className="mb-4 border border-black p-2 rounded-lg"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
						/>
					</div>

					<div className="flex flex-col">
						<label htmlFor="firstname" className="text-left pl-2">
							Prénom
						</label>
						<input
							type="firstname"
							name="firstname"
							id="firstname"
							value={firstname}
							className="mb-4 border border-black p-2 rounded-lg"
							onChange={(e) => setFirstname(e.target.value)}
							required
						/>
					</div>

					<div className="flex flex-col">
						<label htmlFor="lastname" className="text-left pl-2">
							Nom
						</label>
						<input
							type="lastname"
							name="lastname"
							id="lastname"
							value={lastname}
							className="mb-4 border border-black p-2 rounded-lg"
							onChange={(e) => setLastname(e.target.value)}
							required
						/>
					</div>

					<div className="flex flex-col">
						<label htmlFor="password" className="text-left pl-2">
							Mot de passe
						</label>
						<input
							type="password"
							name="password"
							id="password"
							min={6}
							className="mb-4 border border-black p-2 rounded-lg"
							onChange={(e) => setPassword(e.target.value)}
						/>
					</div>

					<div className="flex flex-col">
						<label htmlFor="confirm_password" className="text-left pl-2">
							Confirmez le mot de passe
						</label>
						<input
							type="password"
							name="confirm_password"
							id="confirm_password"
							min={6}
							className="mb-4 border border-black p-2 rounded-lg"
							onChange={(e) => setConfirmPassword(e.target.value)}
						/>
					</div>

					<div className="flex flex-col">
						<label htmlFor="phone_number" className="text-left pl-2">
							Téléphone <small className='text-xs'>(format: +33 1 23 45 67 89)</small>
						</label>
						<input
							type="tel"
							name="phone_number"
							id="phone_number"
							className="mb-4 border border-black p-2 rounded-lg"
							value={phoneNumber}
							onChange={(e) => setPhoneNumber(e.target.value)}
						/>
					</div>

					<div className="flex flex-col">
						<label htmlFor="rate" className="text-left pl-2">
							Role
						</label>
						<select
							id="role"
							className="mb-4 border border-black p-2 rounded-lg"
							name="role"
							onChange={(e) => setRole(e.target.value)}
							value={role}
							required
						>
							<option value="">-- Role --</option>
							<option value="user">Utilisateur</option>
							<option value="admin">Administrateur</option>
							<option value="manager">Manager</option>
							<option value="super-admin">Super administrateur</option>
						</select>
					</div>
				</div>

				<button
					type="submit"
					onClick={handleSubmit}
					className={`transition duration-300 text-white px-4 py-2 rounded-lg 
							bg-primary hover:bg-primary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/70`}
				>
					Modifier l'utilisateur
				</button>
			</form>
		</BOLayout>
	);
};

export default UserCreation;
